/** Session search. Spec 11.4: "Search all sessions; inspect state and event log." */
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { DataTable, type Column } from '../../../components/ui/DataTable';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Field } from '../../../components/ui/Field';
import { Pagination } from '../../../components/ui/Pagination';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { useAdminSessions, type AdminSessionSummary } from '../../../features/admin/api';
import { errorKeyFor } from '../../../lib/errors';
import { useCursorPages } from '../../../lib/pagination';

export const Route = createFileRoute('/$lang/admin/sessions/')({ component: Page });

function Page(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const { lang } = Route.useParams();

  const [query, setQuery] = useState('');
  const pages = useCursorPages();
  const sessions = useAdminSessions({ query, cursor: pages.cursor });

  const columns: readonly Column<AdminSessionSummary>[] = [
    {
      id: 'title',
      header: t('admin:sessions.search'),
      cell: (row) => (
        <Link
          to="/$lang/admin/sessions/$id"
          params={{ lang, id: row.id }}
          className="text-accent underline-offset-4 hover:underline"
        >
          {row.title ?? row.code ?? row.id.slice(0, 8)}
        </Link>
      ),
    },
    {
      id: 'mode',
      header: t('common:dashboard.mode.solo'),
      hideOnMobile: true,
      cell: (row) => row.mode,
    },
    {
      id: 'status',
      header: t('admin:sessions.state'),
      cell: (row) => (
        <Badge tone={row.status === 'active' ? 'positive' : 'neutral'}>{row.status}</Badge>
      ),
    },
    { id: 'seq', header: 'seq', align: 'end', hideOnMobile: true, cell: (row) => row.lastSeq },
    {
      id: 'created',
      header: t('common:dashboard.when'),
      hideOnMobile: true,
      cell: (row) => new Date(row.createdAt).toLocaleString(i18n.language),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-ink">{t('admin:nav.sessions')}</h1>

      <div className="max-w-md">
        <Field
          label={t('admin:sessions.search')}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            pages.reset();
          }}
        />
      </div>

      {sessions.error !== null && <Alert tone="error">{t(errorKeyFor(sessions.error))}</Alert>}

      {sessions.isLoading ? (
        <SkeletonList rows={5} label={t('admin:nav.sessions')} />
      ) : (
        <>
          <DataTable
            caption={t('admin:nav.sessions')}
            columns={columns}
            rows={sessions.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={<EmptyState title={t('admin:sessions.empty')} />}
          />
          <Pagination
            hasPrevious={pages.hasPrevious}
            hasNext={(sessions.data?.nextCursor ?? null) !== null}
            onPrevious={pages.previous}
            onNext={() => {
              pages.next(sessions.data?.nextCursor ?? null);
            }}
            labels={{
              previous: t('common:pagination.previous'),
              next: t('common:pagination.next'),
              nav: t('admin:nav.sessions'),
            }}
          />
        </>
      )}
    </div>
  );
}
